import Chips, { ChipType } from '@/components/Chips/Chips'
import Dropdown from '@/components/Dropdown/Dropdown'
import Icon from '@/components/Icon/Icon'
import Tooltip from '@/components/Tooltip'
import clsx from 'clsx'
import { forwardRef, useEffect, useState } from 'react'
import CancelIcon from '@/assets/svgs/x-mark.svg'
import PropTypes from 'prop-types'

const Input = forwardRef(
  (
    {
      id,
      name,
      type = 'text',
      label,
      placeholder,
      disabled = false,
      required = false,
      errorMessage,
      infoText,
      captionText,
      value = '',
      onCancel,
      onChange,
      onClick,
      variant = 'outlined',
      fullWidth = false,
      size = 'default',
      labelInfoText,
      addClassName,
      prefixIcon,
      suffixIcon,
      prefixDropdown,
      prefixDropdownOption,
      prefixDropdownValue,
      prefixDropdownSelectedOption,
      prefixDropdownOnAdd,
      suffixDropdown,
      suffixDropdownOption,
      suffixDropdownValue,
      suffixDropdownSelectedOption,
      suffixDropdownOnAdd,
      chipInputMultiple,
      chipValues,
      setChipValues,
      allowChipModify,
      onClearChips,
      onBlur,
      disableClear = false,
      register,
      pipeText,
      keyPress,
      setValue,
      inputHeight = false,
      ...inputProps
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState(value)

    useEffect(() => {
      setInputValue(value)
    }, [value])

    const chipInput = chipValues ? true : false

    function handleOnChange(e) {
      const newValue = e.target.value
      if (onChange) {
        onChange(newValue)
      }

      setInputValue(newValue)
    }

    function handleOnClear(e) {
      e.stopPropagation()

      if (onChange) {
        onChange('')
      }

      setInputValue('')
      if (setValue) {
        setValue(name, '', { shouldValidate: true })
      }
    }

    function handleKeyPress(e) {
      if (e.key === 'Enter' && inputValue && chipInput && !chipInputMultiple && allowChipModify) {
        e.preventDefault()
        allowChipModify(inputValue)
        setInputValue('')
      }

      if (keyPress) keyPress(e)
    }

    function handleClearChip(id) {
      const newChipValues = chipValues?.filter(chip => chip.id !== id) || []
      if (setChipValues) {
        setChipValues(newChipValues)
      }
    }

    function handleClearAllChip() {
      if (setChipValues) {
        setChipValues([])
      }
      if (onClearChips) {
        onClearChips()
      }
    }

    const isLabelDisabled =
      (!chipInput && !inputValue && disabled) || (chipInput && disabled && chipValues?.length === 0)

    const inputClass = clsx('relative w-full', addClassName, {
      'cursor-not-allowed': disabled,
    })

    const inputContainerClass = clsx({
      // Non-chip container
      'flex items-center box-border rounded': !chipInput,
      'h-6': !chipInput && size === 'small' && !inputHeight,
      'h-9': !chipInput && size !== 'small' && !inputHeight,
      'h-[43px]': !chipInput && inputHeight,
      // Chip containers
      'flex flex-wrap items-center overflow-x-hidden overflow-y-auto box-border min-h-9 p-2 rounded gap-1':
        chipInput && !chipInputMultiple,
      'flex items-center box-border h-9 p-2 rounded gap-1 min-w-0 overflow-hidden':
        chipInput && chipInputMultiple,
      // Outlined variant
      'border bg-white': variant === 'outlined' && !disabled,
      'border bg-grey-98': variant === 'outlined' && disabled,
      'border-grey-80 hover:border-grey-70 focus-within:border-theme-50':
        variant === 'outlined' && !disabled && !errorMessage && !infoText,
      '[&:not(:focus-within)]:border-grey-50':
        variant === 'outlined' && !disabled && !errorMessage && !infoText && !!inputValue,
      'border-red-50 hover:border-red-50': variant === 'outlined' && !!errorMessage,
      'border-blue-50 hover:border-blue-50': variant === 'outlined' && !!infoText && !errorMessage,
      'border-grey-90 hover:border-grey-90 focus-within:border-grey-90':
        variant === 'outlined' && disabled && !errorMessage && !infoText,
      // Filled variant
      'border-none bg-grey-98': variant === 'filled',
      'hover:bg-grey-95 focus-within:bg-grey-90': variant === 'filled' && !disabled,
    })

    const inputFieldClass = clsx({
      'flex items-center grow my-2 mx-3': !chipInput,
      'flex items-center grow': chipInput,
      'border-l border-l-grey-90 ml-0 pl-1.5': prefixDropdown,
      'focus-within:border-l-grey-50': prefixDropdown && variant === 'filled',
      'border-r border-r-grey-90 mr-0 pr-1.5 max-phone:pr-0.5':
        (suffixIcon && inputValue) || suffixDropdown,
      'focus-within:border-r-grey-50':
        ((suffixIcon && inputValue) || suffixDropdown) && variant === 'filled',
    })

    const inputLabelClass = clsx(
      'flex items-center gap-1',
      size === 'small' ? 'type-micro-medium mb-0.5' : 'type-meta-medium mb-1',
      isLabelDisabled ? 'text-grey-80' : 'text-grey-50'
    )

    const placeHolderText =
      (!chipInputMultiple && (chipValues?.length ?? 0) > 0) ||
      (chipInputMultiple && (chipValues?.length ?? 0) > 0)
        ? ''
        : placeholder || 'Select ...'

    return (
      <div className={inputClass} data-testid="input-field">
        {label && (
          <label htmlFor={id} className={inputLabelClass} data-testid="input-field-label">
            {required && (
              <span className={isLabelDisabled ? 'text-grey-80' : 'text-red-50'}>*</span>
            )}
            {label}
            {labelInfoText && (
              <Tooltip id={labelInfoText} content={labelInfoText}>
                <Icon name="information-circle" style="solid" size="small" />
              </Tooltip>
            )}
          </label>
        )}
        <div className={inputContainerClass}>
          {prefixIcon && <div className="flex items-center pl-3">{prefixIcon}</div>}
          {prefixDropdown && (
            <Dropdown
              options={prefixDropdownOption}
              value={prefixDropdownSelectedOption}
              onAdd={prefixDropdownOnAdd}
              trigger={
                <div
                  className={clsx(
                    'flex items-center justify-center min-w-9 py-2 pl-2 pr-1.5 gap-0.5',
                    size === 'small' ? 'type-meta-medium' : 'type-body-1',
                    disabled ? 'text-grey-80' : 'text-grey-50'
                  )}
                  data-testid="input-prefix-dropdown"
                >
                  <span>{prefixDropdownValue}</span>
                  <Icon name="chevron-down" style="solid" size="small" disabled={disabled} />
                </div>
              }
              multiple={false}
              disabled={disabled}
              offset={size === 'small' ? [0, -2] : [0, 4]}
              size={size}
            />
          )}

          {chipInput &&
            (chipInputMultiple && chipValues?.length ? (
              <Chips
                label={
                  chipValues.length === 1 ? chipValues[0].label : `${chipValues.length} Selected`
                }
                size="small"
                onRemove={() => handleClearAllChip()}
                readOnly={disabled}
                type={ChipType.INPUT}
              />
            ) : (
              chipValues?.map(chip => (
                <Chips
                  key={chip.id}
                  label={chip.label}
                  size="small"
                  onRemove={() => handleClearChip(chip.id)}
                  readOnly={disabled}
                  type={ChipType.INPUT}
                />
              ))
            ))}

          <div className={inputFieldClass}>
            <input
              id={id}
              name={name}
              {...(!register && { value: inputValue })}
              type={type}
              placeholder={placeHolderText}
              readOnly={disabled}
              onChange={handleOnChange}
              onKeyDown={handleKeyPress}
              onClick={e => {
                e.stopPropagation()
                if (onClick) {
                  onClick(e)
                }
              }}
              className={clsx(
                'grow w-full p-0 border-0 outline-none bg-transparent font-avenir text-grey-10',
                size === 'small' ? 'type-meta-medium' : 'type-body-2',
                disabled
                  ? 'cursor-not-allowed placeholder:text-grey-80'
                  : 'cursor-text placeholder:text-grey-50'
              )}
              data-testid="input-field__input"
              ref={register ?? ref}
              autoComplete="off"
              {...inputProps}
            />
            {inputValue &&
              !disabled &&
              !disableClear &&
              ((chipInput && chipInputMultiple) || !chipInput) && (
                <div
                  className="flex items-center shrink-0 cursor-pointer pr-0.5"
                  onClick={handleOnClear}
                >
                  <img
                    src={CancelIcon}
                    alt="Cancel"
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                </div>
              )}
          </div>

          {suffixIcon && (
            <div
              className="py-2 pl-1.5 pr-2 max-phone:pl-1 max-phone:pr-1 cursor-pointer"
              onClick={e => {
                if (onClick) onClick(e)
              }}
            >
              {suffixIcon}
            </div>
          )}
          {suffixDropdown && (
            <Dropdown
              options={suffixDropdownOption}
              value={suffixDropdownSelectedOption}
              onAdd={suffixDropdownOnAdd}
              trigger={
                <div
                  className={clsx(
                    'flex items-center justify-center min-w-9 py-2 pl-1.5 pr-2 gap-0.5',
                    size === 'small' ? 'type-meta-medium' : 'type-body-1',
                    disabled ? 'text-grey-80' : 'text-grey-50'
                  )}
                  data-testid="input-suffix-dropdown"
                >
                  {suffixDropdownValue}
                  <Icon name="chevron-down" style="solid" size="small" disabled={disabled} />
                </div>
              }
              multiple={false}
              placement={'bottom-end'}
              offset={size === 'small' ? [0, -2] : [0, 4]}
              disabled={disabled}
              size={size}
            />
          )}
        </div>
        {pipeText && (
          <div>
            <span>{pipeText}</span>
          </div>
        )}
        <div
          className={clsx(
            'flex content-center gap-1 type-meta-medium',
            size === 'small' ? 'mt-0.5' : 'mt-1'
          )}
        >
          {errorMessage && (
            <>
              <Icon name="exclamation-circle" style="solid" size="small" color="#b4371b" />
              <div id={`${id}-error`} className="text-red-50">
                {errorMessage}
              </div>
            </>
          )}
          {infoText && (
            <>
              <Icon name="information-circle" style="solid" size="small" color="#0061c1" />
              <div id={`${id}-help`} className="text-blue-50">
                {infoText}
              </div>
            </>
          )}
          {captionText}
        </div>
      </div>
    )
  }
)

Input.displayName = 'Input'

Input.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  errorMessage: PropTypes.string,
  infoText: PropTypes.string,
  captionText: PropTypes.string,
  value: PropTypes.string,
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  variant: PropTypes.string,
  fullWidth: PropTypes.bool,
  size: PropTypes.string,
  labelInfoText: PropTypes.string,
  addClassName: PropTypes.string,
  prefixIcon: PropTypes.node,
  suffixIcon: PropTypes.node,
  prefixDropdown: PropTypes.bool,
  prefixDropdownOption: PropTypes.array,
  prefixDropdownValue: PropTypes.string,
  prefixDropdownSelectedOption: PropTypes.string,
  prefixDropdownOnAdd: PropTypes.func,
  suffixDropdown: PropTypes.bool,
  suffixDropdownOption: PropTypes.array,
  suffixDropdownValue: PropTypes.string,
  suffixDropdownSelectedOption: PropTypes.string,
  suffixDropdownOnAdd: PropTypes.func,
  chipInputMultiple: PropTypes.bool,
  chipValues: PropTypes.array,
  setChipValues: PropTypes.func,
  allowChipModify: PropTypes.bool,
  onClearChips: PropTypes.func,
  onBlur: PropTypes.func,
  disableClear: PropTypes.bool,
  register: PropTypes.func,
  pipeText: PropTypes.string,
  keyPress: PropTypes.func,
  setValue: PropTypes.func,
  inputHeight: PropTypes.bool,
}

export default Input
